/** One-shot file attachments added via the composer "+" button.
 *
 * Attachments are plain workspace files the agent can read/write, but they
 * live under a reserved `attachments/` prefix that folder-sync deliberately
 * ignores (see use-doc-sync): a connected folder mirrors the workspace root,
 * so without this carve-out it would treat attachments as "server-only" and
 * delete them — and vice versa. Unlike a folder, attachments are uploaded
 * once and never synced afterwards.
 *
 * Keep ATTACHMENT_PREFIX in sync with ATTACHMENTS_DIR on the backend.
 */

import { uploadFiles, type UploadEntry } from "../api/files";

export const ATTACHMENT_PREFIX = "вложения/";
const ATTACHMENT_DIR = ATTACHMENT_PREFIX.slice(0, -1);

export function isAttachmentPath(path: string): boolean {
  return path === ATTACHMENT_DIR || path.startsWith(ATTACHMENT_PREFIX);
}

/** Upload picked files into the session's `attachments/` dir. Batched so a
 *  large multi-select still reports honest progress (after each batch hits
 *  the wire, not after the read). No sync snapshot — these are fire-and-forget. */
export async function uploadAttachmentsToSession(
  sessionId: string,
  files: File[],
  onProgress?: (current: number, total: number) => void,
): Promise<void> {
  const total = files.length;
  if (total === 0) return;
  onProgress?.(0, total);
  const BATCH = 5;
  for (let i = 0; i < total; i += BATCH) {
    const batch: UploadEntry[] = files
      .slice(i, i + BATCH)
      .map((f) => ({ path: `${ATTACHMENT_PREFIX}${f.name}`, data: f }));
    await uploadFiles(sessionId, batch);
    onProgress?.(Math.min(i + BATCH, total), total);
  }
}
